/**
 * Login Page
 * Handles user authentication
 * - Dev mode: Allows testing with different internal users + external user forms
 * - Production: Azure AD (internal) or embedded Keycloak login/register (external)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, extractApiData } from '../utils/apiClient';
import logger from '../utils/logger';
import { getBrandedHost } from '../utils/tenantDomainMap';
import { generateThemeFromTenantConfig, darkenColor } from '../utils/themeGenerator';

const LoginPage = ({ onLogin, onKeycloakLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [devEmail, setDevEmail] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loginTab, setLoginTab] = useState(0); // 0 = Internal, 1 = Client Portal
  const [externalMode, setExternalMode] = useState('login'); // 'login' or 'register'

  // External login fields
  const [extEmail, setExtEmail] = useState('');
  const [extPassword, setExtPassword] = useState('');

  // External registration fields
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regCompanyName, setRegCompanyName] = useState('');
  const [regPhone, setRegPhone] = useState('');

  // Dev mode toggle - can be overridden by user
  const isDevEnv = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';
  const [devModeEnabled, setDevModeEnabled] = useState(isDevEnv);
  const isDev = devModeEnabled;

  // Per-domain branding for the login page. Resolved by the SERVER from this app's
  // hostname (the branding endpoint reads the tenant's own config). null => no branded
  // domain matched => keep the neutral default logo/title/theme. Failure is a silent
  // no-op for the same reason.
  const [branding, setBranding] = useState(null);
  // MUI theme generated from the branding (applied to the login card only, via a local
  // ThemeProvider). null => the app's default theme is used unchanged.
  const [brandedTheme, setBrandedTheme] = useState(null);

  // Tenant code for external-user registration. Prefer the code the SERVER resolved
  // from this branded domain (authoritative, environment-correct), then an explicit
  // ?tenant= override for dev/testing. We deliberately do NOT parse the subdomain
  // ourselves — the production domains are full custom domains with no tenant label,
  // and the server owns the domain->tenant mapping.
  const tenantCode = branding?.code || searchParams.get('tenant') || null;

  // Check for error from redirect
  useEffect(() => {
    const authError = searchParams.get('error');
    if (authError === 'pending_approval') {
      navigate('/pending-approval');
    } else if (authError === 'not_authorized') {
      setError('Your account is not authorized. Please contact your administrator.');
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (isDev) {
      loadAvailableUsers();
    }
  }, [isDev]);

  // Fetch per-domain branding for the login page. We only call the public branding
  // endpoint when this app is served from a known branded host (or an explicit
  // ?tenant= override is present for testing) — otherwise we'd 404 on every plain/dev
  // host. The SERVER resolves the tenant from the hostname and returns the tenant's own
  // system name / logo / theme. Any failure (404, network, no domain set) is a silent
  // no-op so the neutral default branding stays in place.
  useEffect(() => {
    let cancelled = false;
    const brandedHost = getBrandedHost();
    const tenantOverride = searchParams.get('tenant');
    const domainOverride = searchParams.get('domain'); // dev/testing convenience
    if (!brandedHost && !tenantOverride && !domainOverride) {
      return undefined; // not a branded domain — keep default branding
    }

    const params = new URLSearchParams();
    // Always send the hostname; the server owns the domain->tenant mapping.
    params.set('domain', domainOverride || brandedHost || window.location.hostname);
    if (tenantOverride) params.set('tenant', tenantOverride);

    apiGet(`/public/branding?${params.toString()}`)
      .then((res) => {
        if (cancelled || !res?.success || !res.data) return;
        const data = res.data;
        setBranding(data);
        // Generate a MUI theme from the tenant config (same generator TenantContext
        // feeds post-login) and apply it to the login card via a local ThemeProvider.
        if (data.theme?.primaryColor) {
          try {
            const t = generateThemeFromTenantConfig(data.theme);
            if (t) setBrandedTheme(t);
          } catch (e) { /* keep default theme */ }
        }
        // Per-tenant browser tab title (one shared build => set at runtime).
        if (data.systemName) {
          document.title = data.systemName;
        }
      })
      .catch(() => { /* 404 / no domain set / offline => keep default branding */ });

    return () => { cancelled = true; };
  }, [searchParams]);

  const loadAvailableUsers = async () => {
    try {
      const response = await apiGet('/admin/authorized-users');
      setAvailableUsers(extractApiData(response));
      const defaultEmail = process.env.REACT_APP_DEV_USER_EMAIL;
      if (defaultEmail) {
        setDevEmail(defaultEmail);
      }
    } catch (err) {
      // Silently fail if we can't load users (e.g., not authenticated yet)
      // This is expected when the endpoint requires authentication
      logger.debug('Could not load user list (not authenticated)');
      const defaultEmail = process.env.REACT_APP_DEV_USER_EMAIL;
      if (defaultEmail) {
        setDevEmail(defaultEmail);
      }
    }
  };

  const handleDevLogin = async () => {
    if (!devEmail) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (onLogin) {
        const success = await onLogin(devEmail);
        if (success) {
          navigate('/');
        } else {
          setError('User not found or not authorized. Please check the email address.');
        }
      }
    } catch (err) {
      setError('Failed to verify user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAzureLogin = () => {
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=/';
  };

  const handleExternalLogin = async () => {
    if (!extEmail || !extPassword) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiPost('/auth/login', {
        email: extEmail,
        password: extPassword,
      });

      if (response.success) {
        // Store tokens
        sessionStorage.setItem('keycloakToken', response.accessToken);
        sessionStorage.setItem('keycloakRefreshToken', response.refreshToken);
        sessionStorage.setItem('userType', 'external_client');
        sessionStorage.setItem('externalUser', JSON.stringify(response.user));

        if (onKeycloakLogin) {
          const success = await onKeycloakLogin(response.user);
          if (success) {
            // Small delay to ensure React state updates are processed
            setTimeout(() => {
              navigate('/client-portal/dashboard');
            }, 100);
          }
        }
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.pendingApproval) {
        navigate('/pending-approval');
        return;
      }
      setError(data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleExternalRegister = async () => {
    setError(null);

    if (!regEmail || !regPassword || !regFirstName || !regLastName) {
      setError('Please fill in all required fields');
      return;
    }

    if (regPassword !== regConfirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (regPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await apiPost('/auth/register', {
        email: regEmail,
        password: regPassword,
        firstName: regFirstName,
        lastName: regLastName,
        companyName: regCompanyName,
        phone: regPhone,
        tenantCode: tenantCode,
      });

      if (response.success) {
        setSuccess(response.message || 'Registration successful. Your account is pending approval.');
        // Clear form
        setRegEmail('');
        setRegPassword('');
        setRegConfirmPassword('');
        setRegFirstName('');
        setRegLastName('');
        setRegCompanyName('');
        setRegPhone('');
        // Switch to login after short delay
        setTimeout(() => setExternalMode('login'), 3000);
      }
    } catch (err) {
      const data = err.response?.data;
      setError(data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderExternalLogin = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Sign in to your investment portal
      </Typography>

      <TextField
        fullWidth
        label="Email Address"
        type="email"
        value={extEmail}
        onChange={(e) => setExtEmail(e.target.value)}
        placeholder="your@email.com"
        sx={{ mb: 2 }}
        disabled={loading}
        onKeyDown={(e) => e.key === 'Enter' && handleExternalLogin()}
      />

      <TextField
        fullWidth
        label="Password"
        type="password"
        value={extPassword}
        onChange={(e) => setExtPassword(e.target.value)}
        sx={{ mb: 3 }}
        disabled={loading}
        onKeyDown={(e) => e.key === 'Enter' && handleExternalLogin()}
      />

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={handleExternalLogin}
        disabled={loading || !extEmail || !extPassword}
        sx={{ py: 1.5, mb: 2, backgroundColor: buttonColor, '&:hover': { backgroundColor: buttonHoverColor } }}
      >
        {loading ? <CircularProgress size={24} /> : 'Sign In'}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Don't have an account?{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => { setExternalMode('register'); setError(null); setSuccess(null); }}
          >
            Register here
          </Link>
        </Typography>
      </Box>
    </>
  );

  const renderExternalRegister = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Create your investor portal account
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="First Name *"
          value={regFirstName}
          onChange={(e) => setRegFirstName(e.target.value)}
          disabled={loading}
        />
        <TextField
          fullWidth
          label="Last Name *"
          value={regLastName}
          onChange={(e) => setRegLastName(e.target.value)}
          disabled={loading}
        />
      </Box>

      <TextField
        fullWidth
        label="Email Address *"
        type="email"
        value={regEmail}
        onChange={(e) => setRegEmail(e.target.value)}
        placeholder="your@email.com"
        sx={{ mb: 2 }}
        disabled={loading}
      />

      <TextField
        fullWidth
        label="Company Name"
        value={regCompanyName}
        onChange={(e) => setRegCompanyName(e.target.value)}
        sx={{ mb: 2 }}
        disabled={loading}
      />

      <TextField
        fullWidth
        label="Phone Number"
        value={regPhone}
        onChange={(e) => setRegPhone(e.target.value)}
        sx={{ mb: 2 }}
        disabled={loading}
      />

      <TextField
        fullWidth
        label="Password *"
        type="password"
        value={regPassword}
        onChange={(e) => setRegPassword(e.target.value)}
        helperText="Minimum 8 characters"
        sx={{ mb: 2 }}
        disabled={loading}
      />

      <TextField
        fullWidth
        label="Confirm Password *"
        type="password"
        value={regConfirmPassword}
        onChange={(e) => setRegConfirmPassword(e.target.value)}
        error={regConfirmPassword.length > 0 && regPassword !== regConfirmPassword}
        helperText={regConfirmPassword.length > 0 && regPassword !== regConfirmPassword ? 'Passwords do not match' : ''}
        sx={{ mb: 3 }}
        disabled={loading}
      />

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={handleExternalRegister}
        disabled={loading || !regEmail || !regPassword || !regFirstName || !regLastName}
        sx={{ py: 1.5, mb: 2, backgroundColor: buttonColor, '&:hover': { backgroundColor: buttonHoverColor } }}
      >
        {loading ? <CircularProgress size={24} /> : 'Register'}
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
        Registration requires administrator approval
      </Typography>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Already have an account?{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => { setExternalMode('login'); setError(null); setSuccess(null); }}
          >
            Sign in
          </Link>
        </Typography>
      </Box>
    </>
  );

  // Login-page background. Default is the neutral dark gradient + neutral-blue accent
  // glows. On a branded host we derive a DARK gradient from the tenant's colours (so the
  // white card stays legible regardless of how bright the tenant palette is) and tint the
  // SVG accent ellipses with the tenant primary/secondary. Faint line paths stay neutral.
  const hexToRgbStr = (hex) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    return m ? `${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)}` : null;
  };

  const t = branding?.theme;
  let bgGradient = 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)';
  let ellipse1 = 'rgba(59,130,246,0.08)';
  let ellipse2 = 'rgba(99,102,241,0.07)';
  // Primary action colour for the sign-in buttons + tab selection. Defaults to the
  // dark navy used across the login card; on a branded host it follows the tenant
  // primary (slightly darkened so it reads as a solid CTA, with a darker hover).
  let buttonColor = '#1e293b';
  let buttonHoverColor = '#0f172a';
  if (t?.primaryColor) {
    // Push tenant colours into the deep navy/slate luminance band of the default.
    const edge = darkenColor(t.primaryColor, 78);
    const mid = darkenColor(t.secondaryColor || t.primaryColor, 70);
    bgGradient = `linear-gradient(135deg, ${edge} 0%, ${mid} 50%, ${edge} 100%)`;
    const primRgb = hexToRgbStr(t.primaryColor);
    const secRgb = hexToRgbStr(t.secondaryColor || t.primaryColor);
    if (primRgb) ellipse1 = `rgba(${primRgb},0.10)`;
    if (secRgb) ellipse2 = `rgba(${secRgb},0.09)`;
    buttonColor = t.primaryDark || darkenColor(t.primaryColor, 15);
    buttonHoverColor = darkenColor(t.primaryColor, 35);
  }

  // Show the Client Portal tab unless a branded tenant resolved AND its client_portal
  // module is explicitly disabled. On a default/unbranded host (no branding) we can't
  // know module state, so both tabs remain (current behaviour preserved).
  const showClientPortal = !branding || branding.clientPortalEnabled !== false;
  const overlaySvg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 800'%3E%3Cpath d='M0 400 Q150 320 300 420 T600 360 T900 440 T1200 380' fill='none' stroke='rgba(148,163,184,0.18)' stroke-width='1.5'/%3E%3Cpath d='M0 430 Q180 350 360 460 T720 390 T1080 470 T1200 420' fill='none' stroke='rgba(148,163,184,0.15)' stroke-width='1.2'/%3E%3Cpath d='M0 370 Q120 300 240 380 T480 340 T720 400 T960 350 T1200 370' fill='none' stroke='rgba(148,163,184,0.12)' stroke-width='1'/%3E%3Cpath d='M0 460 Q200 390 400 490 T800 420 T1200 470' fill='none' stroke='rgba(148,163,184,0.15)' stroke-width='1.2'/%3E%3Cpath d='M0 340 Q100 280 200 350 T400 310 T600 360 T800 320 T1000 370 T1200 340' fill='none' stroke='rgba(148,163,184,0.10)' stroke-width='1'/%3E%3Cpath d='M0 490 Q250 440 500 520 T1000 480 T1200 500' fill='none' stroke='rgba(148,163,184,0.12)' stroke-width='1'/%3E%3Cpath d='M0 310 Q300 260 600 330 T1200 300' fill='none' stroke='rgba(148,163,184,0.09)' stroke-width='1'/%3E%3Cpath d='M0 520 Q200 480 400 550 T800 510 T1200 540' fill='none' stroke='rgba(148,163,184,0.09)' stroke-width='1'/%3E%3Cellipse cx='150' cy='200' rx='350' ry='250' fill='${ellipse1}' /%3E%3Cellipse cx='950' cy='600' rx='400' ry='300' fill='${ellipse2}' /%3E%3Cellipse cx='600' cy='80' rx='250' ry='180' fill='rgba(148,163,184,0.06)' /%3E%3C/svg%3E")`;

  const pageContent = (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: bgGradient,
        position: 'relative',
        overflow: 'hidden',
        padding: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: overlaySvg,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          pointerEvents: 'none',
        },
      }}
    >
      <Card sx={{
        maxWidth: 460,
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 20px 60px rgba(0,0,0,0.3)',
        position: 'relative',
        zIndex: 1,
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src={branding?.theme?.logoUrl || '/logo-full.svg'}
              alt={branding?.name || 'Capital Management'}
              onError={(e) => { e.currentTarget.src = '/logo-full.svg'; }}
              sx={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: 100,
              }}
            />
            {branding?.systemName && (
              <Typography variant="h6" sx={{ mt: 2, fontWeight: 600, color: 'text.primary' }}>
                {branding.systemName}
              </Typography>
            )}
          </Box>

          {/* Dev Mode Toggle - Only show if dev env is enabled */}
          {isDevEnv && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="text"
                color={devModeEnabled ? 'warning' : 'inherit'}
                onClick={() => {
                  setDevModeEnabled(!devModeEnabled);
                  setError(null);
                  setSuccess(null);
                }}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                {devModeEnabled ? '🔧 Switch to Production Mode' : '🔧 Switch to Dev Mode'}
              </Button>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {isDev ? (
            // Development login form
            (<>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Development Mode - Enter user email to login
              </Typography>
              <TextField
                fullWidth
                label="Email Address"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                placeholder="user@company.com"
                sx={{ mb: 2 }}
                disabled={loading}
              />
              {availableUsers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Available users:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                    {availableUsers.slice(0, 5).map(user => (
                      <Button
                        key={user.id}
                        size="small"
                        variant="outlined"
                        onClick={() => setDevEmail(user.email)}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        {user.email.split('@')[0]}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleDevLogin}
                disabled={loading || !devEmail}
                sx={{ mt: 2, backgroundColor: buttonColor, '&:hover': { backgroundColor: buttonHoverColor } }}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In (Dev Mode)'}
              </Button>
              {/* Embedded external login/register forms in dev mode too — hidden when
                  the resolved branded tenant has the client_portal module disabled. */}
              {showClientPortal && (
                <>
                  <Divider sx={{ my: 3 }}>
                    <Typography variant="caption" color="text.secondary">
                      EXTERNAL USERS
                    </Typography>
                  </Divider>
                  {externalMode === 'login' ? renderExternalLogin() : renderExternalRegister()}
                </>
              )}
            </>)
          ) : (
            // Production login. Show Internal/Client Portal tabs only when the Client
            // Portal is available for this tenant; otherwise render Internal Staff alone.
            (<>
              {showClientPortal && (
                <Tabs
                  value={loginTab}
                  onChange={(e, newValue) => { setLoginTab(newValue); setError(null); setSuccess(null); }}
                  variant="fullWidth"
                  sx={{ mb: 3, '& .MuiTab-root.Mui-selected': { color: buttonColor }, '& .MuiTabs-indicator': { backgroundColor: buttonColor } }}
                >
                  <Tab
                    icon={<BusinessIcon />}
                    label="Internal Staff"
                    iconPosition="start"
                  />
                  <Tab
                    icon={<PersonIcon />}
                    label="Client Portal"
                    iconPosition="start"
                  />
                </Tabs>
              )}
              {(!showClientPortal || loginTab === 0) ? (
                // Internal Staff - Azure AD
                (<>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                    Sign in with your corporate Microsoft account
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleAzureLogin}
                    disabled={loading}
                    sx={{ py: 1.5, backgroundColor: buttonColor, '&:hover': { backgroundColor: buttonHoverColor } }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Sign in with Microsoft'}
                  </Button>
                </>)
              ) : (
                // Client Portal - Embedded login/register
                (externalMode === 'login' ? renderExternalLogin() : renderExternalRegister())
              )}
            </>)
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 4 }}
          >
            By signing in, you agree to the terms of use.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  // When a branded domain resolved, render the login card under the tenant's theme.
  // Otherwise fall back to the app's default theme (pageContent unchanged).
  return brandedTheme
    ? <ThemeProvider theme={brandedTheme}>{pageContent}</ThemeProvider>
    : pageContent;
};

export default LoginPage;
